/**
 * True when the PaymentIntent was (or is being) paid with `methodType`.
 * The attached payment method is authoritative: `payment_method_types` lists every
 * method the intent *allows* (card, acss_debit, link, ...), not the one used, so it's
 * only trusted when it's the sole allowed method.
 */
function isPaymentIntentMethod(paymentIntent, methodType) {
	if (!paymentIntent) {
		return false;
	}

	const pm = paymentIntent.payment_method;
	if (pm && typeof pm === 'object') {
		return pm.type === methodType || Boolean(pm[methodType]);
	}

	const types = paymentIntent.payment_method_types;
	return Array.isArray(types) && types.length === 1 && types[0] === methodType;
}

/**
 * Detect US ACH (us_bank_account) on a PaymentIntent for UX (pending messaging, etc.).
 */
export function isUsBankAccountPaymentIntent(paymentIntent) {
	return isPaymentIntentMethod(paymentIntent, 'us_bank_account');
}

/**
 * Detect Canadian pre-authorized debit (acss_debit / PAD) on a PaymentIntent for UX (pending messaging, etc.).
 */
export function isAcssDebitPaymentIntent(paymentIntent) {
	return isPaymentIntentMethod(paymentIntent, 'acss_debit');
}

/**
 * Expand `payment_method` on the PaymentIntent when Stripe returned only an id (needed to detect us_bank_account).
 */
export async function getPaymentIntentExpandedForMethodCheck(
	stripe,
	clientSecret,
	paymentIntent,
) {
	if (!paymentIntent || !stripe || !clientSecret) {
		return paymentIntent;
	}

	if (
		paymentIntent.payment_method &&
		typeof paymentIntent.payment_method === 'object'
	) {
		return paymentIntent;
	}

	const { paymentIntent: retrieved, error } =
		await stripe.retrievePaymentIntent(clientSecret, {
			expand: ['payment_method'],
		});

	if (error || !retrieved) {
		return paymentIntent;
	}

	return retrieved;
}
